import { BasicRadioButton } from 'restaurantAdmin/components/basicRadioButton/BasicRadioButton';
import { ControlledGuestAutocomplete } from 'restaurantAdmin/components/guestAutocomplete/ControlledGuestAutocomplete';
import { Dialog } from '@base-ui/react';
import { Button, ButtonVariants } from '@components/button/Button';
import { CancellationPolicyNotice } from '@components/cancellationPolicyNotice/CancelationPolicyNotice';
import { CardElement } from '@components/cardElement/CardElement';
import { Error } from '@components/error/Error';
import { ControlledFormCheckbox } from '@components/formInputs/ControlledFormCheckbox';
import { ControlledFormCurrencyInput } from '@components/formInputs/ControlledFormCurrencyInput';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormPhoneInput } from '@components/formInputs/ControlledFormPhoneInput';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { isWholeNumberRule } from '@components/formInputs/rules';
import { Icon } from '@components/icon/Icon';
import { Switch } from '@components/switch/Switch';
import { Field } from '@base-ui/react';
import { centsToDollar, wholeDollarsToCents } from '@utils/currency';
import typography from '~styles/typography.scss';
import styles from './ConciergeDrawer.scss';
import { ConciergeReceipt } from './ConciergeReceipt';
import { useAvailabilityDrawerContext } from './state/AvailabilityDrawerContext';
import { CheckoutTypes, useAdminCheckout } from './useAdminCheckout';
import { ControlledGuestTagCombobox } from 'restaurantAdmin/components/guestTags/ControlledGuestTagCombobox';
import { Drawer } from 'restaurantAdmin/components/drawer/Drawer';

export const ConciergeDrawer = () => {
  const { closeDrawer, availabilityData, isOpen } =
    useAvailabilityDrawerContext();

  const {
    addGuest,
    cancellationPolicy,
    control,
    customPriceInDollars,
    handleBookReservationClick,
    handleCardElementOnChange,
    handleCheckoutTypeOnChange,
    isBookButtonDisabled,
    isGuestFieldsDisabled,
    pricingInfo,
    removeGuest,
    responseError,
    selectedCheckoutType,
    isEnterCardDetailsOn,
    setIsEnterCardDetailsOn,
    handleSendPayLinkClick,
    handleCopyPayLinkClick,
    isSendPayLinkPending,
    issuedPayLinkUrl,
  } = useAdminCheckout();

  if (!availabilityData) {
    // No availability selected
    return null;
  }
  const customPriceInCents = Number(customPriceInDollars) * 100;

  const showReceipt =
    pricingInfo &&
    (selectedCheckoutType === CheckoutTypes.FullPrice ||
      selectedCheckoutType === CheckoutTypes.CustomPrice);

  const showFullPriceOption = availabilityData.listingPrice > 0;

  const receiptPrice =
    selectedCheckoutType === CheckoutTypes.FullPrice
      ? availabilityData.listingPrice
      : wholeDollarsToCents(parseInt(customPriceInDollars, 10));

  const isCustomPriceSelected =
    selectedCheckoutType === CheckoutTypes.CustomPrice;

  const isFullPriceSelected = selectedCheckoutType === CheckoutTypes.FullPrice;

  const isPayLinkMode = isFullPriceSelected && !isEnterCardDetailsOn;

  const showCardElement = showReceipt && !isPayLinkMode;

  const handleOpenChange = (
    open: boolean,
    eventDetails: Dialog.Root.ChangeEventDetails,
  ) => {
    if (open) return;
    // Toasts are portaled outside the drawer, so dismissing one registers as an
    // outside press. Keep the drawer open in that case so the issued pay link
    // stays visible for copying.
    if (issuedPayLinkUrl && eventDetails.reason === 'outside-press') return;
    closeDrawer();
  };

  return (
    <Drawer
      side="right"
      open={isOpen}
      modal={false}
      onOpenChange={handleOpenChange}
      drawerContentClassName={styles.drawerWrapper}
    >
      <div
        className={styles.drawer}
        role="dialog"
        aria-labelledby="book_reservation_drawer_label"
      >
        <button
          aria-label="close"
          className={styles.closeButton}
          onClick={closeDrawer}
        >
          <Icon name="close" />
        </button>
        <h2 className={styles.drawerLabel} id="book_reservation_drawer_label">
          Book Reservation
        </h2>
        <form
          className={styles.drawerForm}
          onSubmit={
            isPayLinkMode ? handleSendPayLinkClick : handleBookReservationClick
          }
        >
          <div
            role="group"
            className={styles.checkoutType}
            aria-labelledby="checkout-legend"
          >
            <div id="checkout-legend">Checkout type</div>
            <div className={styles.radioGroup} role="radiogroup">
              {showFullPriceOption && (
                <BasicRadioButton
                  label="Full price"
                  checked={selectedCheckoutType === CheckoutTypes.FullPrice}
                  name="checkoutType"
                  onChange={handleCheckoutTypeOnChange}
                  value={CheckoutTypes.FullPrice}
                />
              )}
              <BasicRadioButton
                label="Free reservation"
                checked={selectedCheckoutType === CheckoutTypes.NoPrice}
                name="checkoutType"
                onChange={handleCheckoutTypeOnChange}
                value={CheckoutTypes.NoPrice}
              />
              <BasicRadioButton
                label="Custom price"
                checked={isCustomPriceSelected}
                name="checkoutType"
                onChange={handleCheckoutTypeOnChange}
                value={CheckoutTypes.CustomPrice}
              />
            </div>
          </div>
          {isCustomPriceSelected && (
            <>
              <ControlledFormCurrencyInput
                control={control}
                label="Custom Price"
                name="customPriceInDollars"
                rules={{
                  min: {
                    message: 'Price must be greater than 0',
                    value: 1,
                  },
                  validate: {
                    isWholeNumber: isWholeNumberRule('Price per table'),
                  },
                  required: true,
                }}
              />
              <div className={styles.originalPrice}>
                <div className={typography.c3}>Original price</div>
                <div className={typography.c3}>
                  {centsToDollar(availabilityData.listingPrice)}
                </div>
              </div>
            </>
          )}
          {showReceipt && (
            <>
              <ConciergeReceipt
                fee={pricingInfo.fee}
                price={receiptPrice}
                priceLabel={
                  isCustomPriceSelected ? 'Custom price' : 'Purchase price'
                }
                tax={pricingInfo.tax}
                total={pricingInfo.total}
              />
              {cancellationPolicy && (
                <CancellationPolicyNotice
                  availabilityTime={availabilityData.time}
                  cancellationFee={cancellationPolicy.fee}
                  date={availabilityData.date}
                  hoursBeforeReservation={
                    cancellationPolicy.hoursBeforeReservation
                  }
                  purchasePrice={
                    isCustomPriceSelected
                      ? customPriceInCents
                      : availabilityData.listingPrice
                  }
                  threshold={cancellationPolicy.threshold}
                />
              )}
              {isFullPriceSelected && (
                <Field.Root className={styles.enterCardDetailsToggleRow}>
                  <Field.Label>Enter card details</Field.Label>
                  <Switch
                    checked={isEnterCardDetailsOn}
                    onCheckedChange={setIsEnterCardDetailsOn}
                  />
                </Field.Root>
              )}
              {showCardElement && (
                <CardElement onChange={handleCardElementOnChange} />
              )}
            </>
          )}
          <ControlledGuestAutocomplete
            control={control}
            name="guestId"
            onSelect={addGuest}
            onClear={removeGuest}
          />
          <div className={styles.guestFields}>
            <ControlledFormInput
              control={control}
              label="First Name"
              name="firstName"
              type="text"
              disabled={isGuestFieldsDisabled}
              rules={{
                required: true,
                validate: (name: string) =>
                  name.trim().length < 1 ? name : 'Must be a valid name',
              }}
            />
            <ControlledFormInput
              control={control}
              label="Last Name"
              name="lastName"
              disabled={isGuestFieldsDisabled}
              type="text"
              rules={{
                required: true,
                validate: (name: string) =>
                  name.trim().length < 1 ? name : 'Must be a valid name',
              }}
            />
          </div>

          <ControlledFormPhoneInput
            control={control}
            label={isPayLinkMode ? 'Guest Phone' : 'Guest Phone (optional)'}
            disabled={isGuestFieldsDisabled}
            name="phone"
            rules={{
              required: isPayLinkMode,
            }}
          />

          <div className={styles.guestTagsField}>
            <label htmlFor={'guestTags'}>Guest Tags (optional)</label>
            <ControlledGuestTagCombobox control={control} name={'guestTags'} />
          </div>

          <ControlledFormTextareaAutosize
            control={control}
            label="Visit Notes (optional)"
            name="staffNote"
            placeholder="Add internal notes (visible only to staff)"
            rules={{
              required: false,
            }}
          />

          {!isPayLinkMode && (
            <ControlledFormCheckbox
              control={control}
              label="Message the guest about this reservation"
              name="messageGuest"
              defaultValue={true}
            />
          )}

          <Error className={styles.error} message={responseError} />
          <div className={styles.formActions}>
            <Button
              label="Cancel"
              onClick={closeDrawer}
              variant={ButtonVariants.Tertiary}
            />
            {isPayLinkMode ? (
              <Button
                isDisabled={isSendPayLinkPending || !!issuedPayLinkUrl}
                label="Send Pay Link"
                type="submit"
                variant={ButtonVariants.Primary}
              />
            ) : (
              <Button
                isDisabled={isBookButtonDisabled}
                label="Book Reservation"
                type="submit"
                variant={ButtonVariants.Primary}
              />
            )}
          </div>
          {issuedPayLinkUrl && (
            <div className={styles.payLinkResult}>
              <a
                className={styles.payLinkUrl}
                href={issuedPayLinkUrl}
                rel="noreferrer"
                target="_blank"
              >
                {issuedPayLinkUrl}
              </a>
              <button
                aria-label="Copy pay link"
                className={styles.copyPayLinkButton}
                onClick={handleCopyPayLinkClick}
                type="button"
              >
                <Icon name="copy" />
              </button>
            </div>
          )}
        </form>
      </div>
    </Drawer>
  );
};
